import React, { useEffect, useState } from 'react';
import { API } from '../../api/client';

// One admin-configured answer-derived column: an application question mapped to
// a table column header. questionId is the column's stable identity.
interface Column {
  questionId: number;
  label: string;
}

// A selectable application question for the picker (flattened from the year's bank).
interface BankQuestion {
  id: number;
  label: string;
  section: string;
}

export default function AdminPipelineColumns() {
  const [columns, setColumns] = useState<Column[]>([]);
  const [bank, setBank] = useState<BankQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newQid, setNewQid] = useState<number | ''>('');
  const [newLabel, setNewLabel] = useState('');

  // Cohort year state.
  const [year, setYear] = useState<number | null>(null);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [creatingYear, setCreatingYear] = useState(false);
  const [newYearValue, setNewYearValue] = useState<number>(0);
  const [yearMsg, setYearMsg] = useState('');
  const [yearErr, setYearErr] = useState('');

  const load = (y?: number) => {
    setLoading(true);
    setAddingNew(false);
    Promise.all([API.adminPipelineColumns(y), API.adminQuestions(y)]).then(([cols, qs]) => {
      if (cols) {
        setColumns(cols.columns ?? []);
        setYear(cols.year ?? null);
        setActiveYear(cols.activeYear ?? null);
        setAvailableYears(cols.availableYears ?? []);
      }
      const flat: BankQuestion[] = [];
      for (const s of qs?.sections ?? []) {
        for (const q of s.questions ?? []) {
          flat.push({ id: q.id, label: q.label, section: s.title ?? s.shortLabel ?? '' });
        }
      }
      setBank(flat);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const flashYear = (msg: string, isErr = false) => {
    if (isErr) { setYearErr(msg); setTimeout(() => setYearErr(''), 4000); }
    else { setYearMsg(msg); setTimeout(() => setYearMsg(''), 3000); }
  };

  const onSelectYear = (y: number) => { if (y !== year) load(y); };

  const startCreateYear = () => {
    const base = availableYears.length ? Math.max(...availableYears) : (activeYear ?? new Date().getFullYear());
    setNewYearValue(base + 1);
    setYearErr('');
    setCreatingYear(true);
  };

  const createYear = async () => {
    try {
      await API.seedPipelineColumns(newYearValue);
      setCreatingYear(false);
      flashYear(`Created ${newYearValue} pipeline columns from the previous year. Edit them below, then save.`);
      load(newYearValue);
    } catch {
      flashYear(`Could not create ${newYearValue} — pipeline columns for that year may already exist.`, true);
    }
  };

  const qLabel = (qid: number): string => bank.find(q => q.id === qid)?.label ?? `Question ${qid}`;

  const setLabel = (qid: number, label: string) => {
    setColumns(prev => prev.map(c => c.questionId === qid ? { ...c, label } : c));
  };

  const deleteColumn = (qid: number) => {
    setColumns(prev => prev.filter(c => c.questionId !== qid));
  };

  const moveColumn = (qid: number, dir: -1 | 1) => {
    setColumns(prev => {
      const cs = [...prev];
      const i = cs.findIndex(c => c.questionId === qid);
      const j = i + dir;
      if (j < 0 || j >= cs.length) return prev;
      [cs[i], cs[j]] = [cs[j], cs[i]];
      return cs;
    });
  };

  const commitNewColumn = () => {
    if (newQid === '' || !newLabel.trim()) return;
    if (columns.some(c => c.questionId === newQid)) {
      setError('That question is already a column.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setColumns(prev => [...prev, { questionId: Number(newQid), label: newLabel.trim() }]);
    setNewQid('');
    setNewLabel('');
    setAddingNew(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await API.updatePipelineColumns({ columns }, year ?? undefined);
      setSavedMsg('Pipeline columns saved.');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  // Questions not already used as a column — the options for the add picker.
  const usedIds = new Set(columns.map(c => c.questionId));
  const available = bank.filter(q => !usedIds.has(q.id));

  return (
    <>
      <div className="phil-page-heading">Pipeline Columns</div>
      <div className="phil-page-sub">
        Choose which applicant-answer columns appear in the Application Pipeline table, and their headers.
        Applies to the FY27+ pipeline layout; earlier cohorts keep their fixed columns. Score, Status and
        Assigned Evaluator columns are always shown and aren't configured here.
      </div>

      {/* Cohort year bar */}
      <div className="aq-yearbar">
        <div className="aq-yearbar-group">
          <label className="aq-yearbar-label">Cohort year</label>
          <select
            className="phil-select"
            style={{ width: 'auto', minWidth: 90 }}
            value={year ?? ''}
            onChange={e => onSelectYear(Number(e.target.value))}
          >
            {year != null && !availableYears.includes(year) && <option value={year}>{year}</option>}
            {availableYears.map(y => (
              <option key={y} value={y}>{y}{y === activeYear ? ' • active' : ''}</option>
            ))}
          </select>
          {year === activeYear ? (
            <span className="aq-active-pill">● Current program year</span>
          ) : (
            <span style={{ fontSize: 12, color: '#888' }}>Not the active year</span>
          )}
        </div>

        <div className="aq-yearbar-group">
          {yearMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{yearMsg}</span>}
          {yearErr && <span style={{ fontSize: 12, color: '#E24B4A' }}>{yearErr}</span>}
          {!creatingYear ? (
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={startCreateYear}>
              + New cohort year
            </button>
          ) : (
            <span className="aq-create-year">
              <input
                type="number"
                className="phil-input"
                style={{ width: 90 }}
                value={newYearValue}
                onChange={e => setNewYearValue(Number(e.target.value))}
              />
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={createYear}>Create</button>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => setCreatingYear(false)}>Cancel</button>
              <span className="aq-create-hint">Copies the previous year's columns as a starting point.</span>
            </span>
          )}
        </div>
      </div>

      <div className="phil-card" style={{ marginTop: 16 }}>
        <div className="phil-card-head">
          <h2>Answer Columns</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {savedMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{savedMsg}</span>}
            {error && <span style={{ fontSize: 12, color: '#E24B4A' }}>{error}</span>}
            <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Columns'}
            </button>
          </div>
        </div>

        <table className="phil-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>#</th>
              <th style={{ width: 220 }}>Column header</th>
              <th>Applicant question (source)</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c, idx) => (
              <tr key={c.questionId}>
                <td style={{ color: '#aaa', fontSize: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <button
                      className="phil-btn phil-btn-secondary phil-btn-sm"
                      style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === 0 ? 'hidden' : 'visible' }}
                      onClick={() => moveColumn(c.questionId, -1)}
                      title="Move left"
                    >▲</button>
                    <span>{idx + 1}</span>
                    <button
                      className="phil-btn phil-btn-secondary phil-btn-sm"
                      style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === columns.length - 1 ? 'hidden' : 'visible' }}
                      onClick={() => moveColumn(c.questionId, 1)}
                      title="Move right"
                    >▼</button>
                  </div>
                </td>
                <td>
                  <input
                    className="phil-input"
                    value={c.label}
                    onChange={e => setLabel(c.questionId, e.target.value)}
                  />
                </td>
                <td style={{ fontSize: 13, color: '#555' }}>
                  <span style={{ color: '#aaa', fontFamily: 'monospace', fontSize: 12, marginRight: 6 }}>Q{c.questionId}</span>
                  {qLabel(c.questionId)}
                </td>
                <td>
                  <button
                    className="phil-btn phil-btn-secondary phil-btn-sm"
                    style={{ color: '#E24B4A' }}
                    onClick={() => deleteColumn(c.questionId)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}

            {columns.length === 0 && !addingNew && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>
                No answer columns configured for this year yet.
              </td></tr>
            )}

            {/* Add new column row */}
            {addingNew && (
              <tr style={{ background: '#f7fbfe' }}>
                <td style={{ color: '#aaa', fontSize: 12 }}>New</td>
                <td>
                  <input
                    className="phil-input"
                    placeholder="Column header"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    autoFocus
                  />
                </td>
                <td>
                  <select
                    className="phil-select"
                    style={{ width: '100%' }}
                    value={newQid}
                    onChange={e => {
                      const qid = Number(e.target.value);
                      setNewQid(qid);
                      // Default the header to the question's own label until edited.
                      if (!newLabel.trim()) setNewLabel(bank.find(q => q.id === qid)?.label ?? '');
                    }}
                  >
                    <option value="">Select an applicant question…</option>
                    {available.map(q => (
                      <option key={q.id} value={q.id}>Q{q.id} — {q.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={commitNewColumn}>Add</button>
                    <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => { setAddingNew(false); setNewQid(''); setNewLabel(''); }}>Cancel</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!addingNew && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f0f0f0' }}>
            <button
              className="phil-btn phil-btn-secondary phil-btn-sm"
              onClick={() => setAddingNew(true)}
              disabled={available.length === 0}
              title={available.length === 0 ? 'All applicant questions are already columns' : undefined}
            >
              + Add Column
            </button>
          </div>
        )}
      </div>
    </>
  );
}
